/** Tokens CIME — `apps/web/src/index.css` (`--cime-urgent` / gold / success). */
const URGENT = [0xc0, 0x5b, 0x33] as const;
const GOLD = [0xc9, 0xa8, 0x4c] as const;
const SUCCESS = [0x16, 0xa3, 0x4a] as const;
const WHITE = [255, 255, 255] as const;
const BLACK = [0, 0, 0] as const;

type Rgb = readonly [number, number, number];

export type DueHeat =
  | { kind: 'done' }
  | {
      kind: 'overdue';
      background: string;
      color: string;
      border: string;
      bordered: true;
    }
  | {
      kind: 'heat';
      step: number;
      background: string;
      color: string;
      border: string;
      bordered: true;
    };

function lerpChannel(a: number, b: number, t: number): number {
  return Math.round(a + (b - a) * t);
}

function mix(a: Rgb, b: Rgb, t: number): [number, number, number] {
  return [lerpChannel(a[0], b[0], t), lerpChannel(a[1], b[1], t), lerpChannel(a[2], b[2], t)];
}

function hex(rgb: Rgb): string {
  return `#${rgb.map((n) => n.toString(16).padStart(2, '0')).join('')}`;
}

/** Palier 1 = aujourd’hui (urgent), palier 15 = 60 j+ (success), via gold. */
function hueForStep(step: number): [number, number, number] {
  const t = (step - 1) / 14;
  if (t <= 0.5) return mix(URGENT, GOLD, t * 2);
  return mix(GOLD, SUCCESS, (t - 0.5) * 2);
}

function pill(rgb: Rgb) {
  return {
    background: hex(mix(rgb, WHITE, 0.86)),
    color: hex(mix(rgb, BLACK, 0.35)),
    border: hex(mix(rgb, WHITE, 0.55)),
    bordered: true as const,
  };
}

export function dueHeatStep(daysUntil: number): number | 'overdue' {
  if (daysUntil < 0) return 'overdue';
  if (daysUntil <= 0) return 1;
  if (daysUntil === 1) return 2;
  if (daysUntil === 2) return 3;
  if (daysUntil === 3) return 4;
  if (daysUntil <= 5) return 5;
  if (daysUntil <= 7) return 6;
  if (daysUntil <= 10) return 7;
  if (daysUntil <= 13) return 8;
  if (daysUntil <= 16) return 9;
  if (daysUntil <= 20) return 10;
  if (daysUntil <= 24) return 11;
  if (daysUntil <= 29) return 12;
  if (daysUntil <= 39) return 13;
  if (daysUntil <= 59) return 14;
  return 15;
}

export function dueHeat(input: {
  daysUntil: number | null;
  isCompleted: boolean;
}): DueHeat | null {
  if (input.isCompleted) return { kind: 'done' };
  if (input.daysUntil === null) return null;

  const step = dueHeatStep(input.daysUntil);
  if (step === 'overdue') {
    const urgent = hex(URGENT);
    return {
      kind: 'overdue',
      background: urgent,
      color: '#ffffff',
      border: urgent,
      bordered: true,
    };
  }

  return { kind: 'heat', step, ...pill(hueForStep(step)) };
}
